<template>
  <div data-test="outside-vif">
    <div
      class="base-table-pager-total"
      v-if="totalItems > 0 && !isBusy && !hidePagerTotal && !hideAllPages"
    >
      <slot name="pager-total">
        Displaying {{ displayingCurrent }} - {{ displayingCurrentTotal }} of
        <strong>{{ totalItems }}</strong> results
      </slot>
    </div>
    <div class="base-table-wrapper">
      <br class="clear" />
      <b-table
        striped
        hover
        :sticky-header="true"
        :no-local-sorting="noLocalSorting"
        :no-sort-reset="true"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        show-empty
        :busy="isBusy"
        :items="items"
        :fields="fields"
        @sort-changed="sortingChangedEvent"
        ref="table"
        :class="tableClasses"
        :current-page="currentPage"
        :per-page="noProviderPaging ? itemsPerPage : 0"
        :no-provider-paging="noProviderPaging"
        :id="tableId"
        :primary-key="primaryKey"
        :key="primaryKey ?? items?.length"
        :table-class="tableClasses"
        :caption-html="tableCaption"
        :caption-top="tableCaptionAbove"
      >
        <template #table-busy>
          <slot name="table-busy">
            <raft-busy busy-text="Loading..." />
          </slot>
        </template>

        <template #empty>
          <slot name="empty">
            <div :class="classPrefix + '-empty-text'">
              There are currently no items to list.
            </div>
          </slot>
        </template>
        <template #head()="data">
          <div v-if="customHeaders.includes(data.column)">
            <slot :name="customHeaderSlotName(data.column)" :data="data" />
          </div>
        </template>
        <template #cell()="data">
          <div v-if="data?.value?.v_html" v-html="getHtmlCell(data.value)" />
          <div v-else-if="data.value.custom_slot">
            <slot :name="data.value.custom_slot" :data="data" />
          </div>
          <div v-else-if="data.field.custom_slot">
            <slot :name="data.field.custom_slot" :data="data" />
          </div>
          <div v-else>
            {{ data.value }}
          </div>
        </template>
      </b-table>

      <b-pagination
        v-if="totalItems > itemsPerPage && !hideAllPages"
        v-model="selectedPage"
        :total-rows="totalItems"
        :per-page="itemsPerPage"
        :aria-controls="tableId"
        limit="10"
        first-text="« first"
        prev-text="‹ previous"
        next-text="next ›"
        last-text="last »"
        align="center"
      />
      <slot name="extra" />
    </div>
  </div>
</template>

<script>
import RaftBusy from "../RaftBusy.vue";

require("../../css/FixTableColumn.css");
require("../../css/B3BaseTable.css");
require("../../css/B3Pagination.css");

export default {
  name: "BaseTable",
  data() {
    return {
      selectedPage: null,
      sortBy: null,
      sortDesc: null,
    };
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    isBusy: {
      type: Boolean,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 25,
    },
    noProviderPaging: {
      type: Boolean,
      default: false,
    },
    classPrefix: {
      type: String,
      default: "table",
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
    responsiveTable: {
      type: Boolean,
      default: false,
    },
    customHeaders: {
      type: Array,
      default: () => [],
    },
    noLocalSorting: {
      type: Boolean,
      default: true,
    },
    tablePageAt: {
      type: Number,
      default: 0,
    },
    tableId: {
      type: String,
      default: "basetable",
    },
    primaryKey: {
      type: String,
      default: null,
    },
    hidePagerTotal: {
      type: Boolean,
      default: false,
    },
    defaultSort: {
      type: String,
      default: "",
    },
    tableCaption: {
      type: String,
      default: null,
    },
    tableCaptionAbove: {
      type: Boolean,
      default: true,
    },
    hideAllPages: { type: Boolean, default: false },
  },
  components: {
    RaftBusy,
  },
  computed: {
    tableClasses: function () {
      const classes = {
        "table-pause-scroll": this.isBusy || this.items?.length < 1,
        "table-responsive": this.responsiveTable,
        "table-fullscreen": !this.responsiveTable,
        "table-data-loaded": this.items?.length > 0,
      };
      classes[this.classPrefix + "-table"] = true;
      return classes;
    },
    displayingCurrent: function () {
      if (this.currentPage > 1) {
        return (this.currentPage - 1) * this.itemsPerPage + 1;
      }
      return 1;
    },
    displayingCurrentTotal: function () {
      if (this.totalItems < this.itemsPerPage) {
        return this.totalItems;
      }
      if (this.totalPages === this.currentPage) {
        return this.totalItems;
      }
      return this.displayingCurrent + this.itemsPerPage - 1;
    },
    totalPages: function () {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
  },
  methods: {
    sortingChangedEvent: function (sortEvent) {
      this.$emit("sort-changed", sortEvent);
    },
    getHtmlCell: function (value) {
      if (
        value !== null &&
        typeof value !== "undefined" &&
        typeof value.v_html !== "undefined"
      ) {
        return value.v_html;
      }
      return "";
    },
    customHeaderDefault() {
      return [];
    },
    customHeaderSlotName(header) {
      return header + "-header";
    },
  },
  mounted() {
    this.selectedPage = this.currentPage;
    if (this.defaultSort) {
      this.sortBy = this.defaultSort;
    }
  },
  watch: {
    selectedPage: function (newPage) {
      this.$emit("update-current-page", newPage);
    },
  },
};
</script>

<style scoped>
.clear {
  clear: both;
}

.base-table-wrapper {
  overflow-x: scroll;
  overflow-y: auto;
}

.page-link {
  cursor: pointer;
}

.base-table-wrapper :deep(caption) {
  text-align: left;
  font-weight: bold;
}

:deep(div[id$="-filter-label"]) {
  min-width: 115px;
  display: inline-block;
}

.base-table-pager-total {
  margin-right: 0;
  text-align: right;
  margin-bottom: -1em;
}

:deep(.mr-sm-5) {
  width: 95%;
}

:deep(.flex-row) {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
